"use client";

import { useMemo, useState } from "react";
import { ParagraphHeading } from "../shared/ParagraphHeading";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { ParagraphContactForm as ParagraphContactFormData } from "@/lib/api/cms-catalog";
import {
  Field,
  Input,
  Textarea,
  Checkbox,
  FormStatus,
  Honeypot,
  SubmitButton,
} from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "../Paragraph.module.css";

/**
 * Layout 22 - `PARAGRAPH_CONTACT_FORM_LAYOUT`: formularz kontaktowy w akapicie.
 *
 * **Wzorowane na `CContactForm`** (anti-spam, RHF + Zod, form primitives) ale
 * uproszczone do MVP set pól (phone/email/text + RODO). Akapit nie ma
 * configowalnej listy pól per okno - używa stałego set'u.
 *
 * **POST endpoint:** `/api/c-descriptive-page/paragraph-contact-send` ->
 * `IAPI_paragraphContactSend` w `C_DESCRIPTIVE_PAGE_CONTROLLER`.
 *
 * **Anti-spam (4 warstwy):** HMAC token + min-delay 5s + honeypot `subject`
 * + rate-limit per IP (server-side).
 *
 * **Reuse standardów komunikatów** (memory `messages_system_standard`):
 *   - per-pole błędy -> `<Field error>` inline
 *   - top banner systemowe (rate limit, token expired, sieć) -> `<FormStatus>`
 *   - success -> `toast.success` (transient, user nie traci kontekstu formularza)
 */
export function ParagraphContactForm({
  paragraph,
  t,
}: {
  paragraph: ParagraphContactFormData;
  t: ParagraphTranslations;
}) {
  const schema = useMemo(() => buildSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { phone: "", email: "", text: "", personal_data: false },
    mode: "onTouched",
  });

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. Scope
     to (idCatalog, idParagraph) - musi pasowac do backend'owego
     `IANTISPAM::verifyToken` w `IAPI_paragraphContactSend`. */
  const antispam = useAntiSpamToken([paragraph.id_catalog, paragraph.id]);

  const showRodo = paragraph.config_form?.show_personal_data !== false;
  const rodoHtml =
    paragraph.config_form?.processing_personal_data || t.contactForm.rodoDefault;

  const onSubmit = async (values: FormValues) => {
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < paragraph.antispam.min_delay_seconds) {
      setTopError(
        t.contactForm.status.waitBeforeSubmit(
          paragraph.antispam.min_delay_seconds - ageSec,
        ),
      );
      return;
    }

    if (!antispam.token || !antispam.timestamp) {
      setTopError(antispam.error ?? t.contactForm.status.sendFailed);
      return;
    }

    const body = {
      id_catalog: paragraph.id_catalog,
      id_paragraph: paragraph.id,
      token: antispam.token,
      timestamp: antispam.timestamp,
      [paragraph.antispam.honeypot_field]: honeypot,
      phone: values.phone,
      email: values.email,
      text: values.text,
      personal_data: values.personal_data === true,
    };

    try {
      const res = await fetch(`/api/c-descriptive-page/paragraph-contact-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        code?: number;
        errors?: Record<string, string>;
      };

      if (!res.ok || !json.ok) {
        if (
          json.errors &&
          typeof json.errors === "object" &&
          Object.keys(json.errors).length > 0
        ) {
          for (const [name, msg] of Object.entries(json.errors)) {
            if (typeof msg !== "string") continue;
            setError(name as keyof FormValues, { type: "server", message: msg });
          }
        } else {
          setTopError(
            json.message ||
              (res.status === 429
                ? t.contactForm.status.rateLimit
                : res.status === 401
                ? t.contactForm.status.tokenExpired
                : t.contactForm.status.sendFailed),
          );
        }
        return;
      }

      toast.success(json.message || t.contactForm.status.sendSuccess);
      reset({ phone: "", email: "", text: "", personal_data: false });
      setHoneypot("");
    } catch (err) {
      setTopError(
        err instanceof Error
          ? t.contactForm.status.networkError(err.message)
          : t.contactForm.status.networkErrorGeneric,
      );
    }
  };

  return (
    <section className={styles.paragraph}>
      <ParagraphHeading name={paragraph.name} centerHeader={paragraph.center_header} />
      <form className={styles.contactForm} onSubmit={handleSubmit(onSubmit)} noValidate>
        {paragraph.text && (
          <div className={styles.contactFormIntro}>
            <HtmlText html={paragraph.text} />
          </div>
        )}

        <div className={styles.contactFormRow}>
          <Field
            label={t.contactForm.labels.phone}
            error={
              typeof errors.phone?.message === "string" ? errors.phone.message : undefined
            }
          >
            <Input
              type="tel"
              autoComplete="tel"
              disabled={isSubmitting}
              {...register("phone")}
            />
          </Field>
          <Field
            label={t.contactForm.labels.email}
            required
            error={
              typeof errors.email?.message === "string" ? errors.email.message : undefined
            }
          >
            <Input
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              {...register("email")}
            />
          </Field>
        </div>

        <Field
          label={t.contactForm.labels.text}
          required
          error={typeof errors.text?.message === "string" ? errors.text.message : undefined}
        >
          <Textarea rows={5} disabled={isSubmitting} {...register("text")} />
        </Field>

        {showRodo && (
          <div>
            <Controller
              name="personal_data"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value === true}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  disabled={isSubmitting}
                  aria-invalid={!!errors.personal_data || undefined}
                >
                  <span dangerouslySetInnerHTML={{ __html: rodoHtml }} />
                </Checkbox>
              )}
            />
            {typeof errors.personal_data?.message === "string" && (
              <div className={styles.contactFormError}>
                {errors.personal_data.message}
              </div>
            )}
          </div>
        )}

        <Honeypot
          name={paragraph.antispam.honeypot_field}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />

        {topError && <FormStatus variant="error">{topError}</FormStatus>}

        <div className={styles.contactFormActions}>
          <SubmitButton
            loading={isSubmitting || !antispam.ready}
            loadingLabel={t.contactForm.sending}
            disabled={antispam.error != null}
          >
            {t.contactForm.submit}
          </SubmitButton>
        </div>
      </form>
    </section>
  );
}

type FormValues = {
  phone: string;
  email: string;
  text: string;
  personal_data: boolean;
};

function buildSchema(t: ParagraphTranslations) {
  return z.object({
    phone: z
      .string()
      .regex(/^[\d +()\-]{6,20}$/, t.contactForm.validation.invalidPhone)
      .or(z.literal("")),
    email: z
      .string()
      .min(1, t.contactForm.validation.requiredField(t.contactForm.labels.email))
      .email(t.contactForm.validation.invalidEmail),
    text: z
      .string()
      .min(1, t.contactForm.validation.requiredField(t.contactForm.labels.text)),
    personal_data: z.boolean(),
  });
}
